import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { useTenant } from '../context/TenantContext.jsx';
import { Link } from 'react-router-dom';
import { CustomersMapModal } from '../components/CustomersMapModal.jsx';
import { CustomerEditModal } from '../components/customers/CustomerEditModal.jsx';

/** Iniciais para o avatar (até 2 letras). */
function initials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Cor estável a partir do nome (para o avatar). */
function avatarHue(name) {
  let h = 0;
  const s = String(name || '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
}

export function CustomersPage() {
  const { tenantId } = useTenant();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [mapOpen, setMapOpen] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    if (!tenantId) return;
    setLoading(true);
    try {
      const query = q ? `?search=${encodeURIComponent(q)}` : '';
      const data = await api(`/tenants/${tenantId}/customers${query}`);
      setItems(data.customers || []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, q]);

  async function toggleCatalogAccess(customer, allow) {
    if (!tenantId) return;
    setSavingId(customer.id);
    setItems((prev) =>
      prev.map((it) => (it.id === customer.id ? { ...it, can_download_catalog: allow } : it)),
    );
    try {
      await api(`/tenants/${tenantId}/customers/${customer.id}/catalog-access`, {
        method: 'PATCH',
        body: JSON.stringify({ can_download_catalog: allow }),
      });
    } catch (e) {
      setItems((prev) =>
        prev.map((it) => (it.id === customer.id ? { ...it, can_download_catalog: !allow } : it)),
      );
      window.alert(e?.message || 'Não foi possível atualizar a permissão.');
    } finally {
      setSavingId(null);
    }
  }

  function handleSaved(updated) {
    setItems((prev) =>
      prev.map((it) => (it.id === updated.id ? { ...it, ...updated } : it)),
    );
    setEditing(null);
  }

  if (!tenantId) {
    return (
      <div className="py-12 text-center text-sm text-ink/60 dark:text-frost-muted">
        Selecione uma empresa em{' '}
        <Link className="font-semibold text-accent" to="/app/empresa">
          Empresas
        </Link>
        .
      </div>
    );
  }

  return (
    <div className="space-y-6 py-8">
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-3xl font-semibold">Clientes</h2>
            <p className="mt-1 text-sm text-ink/50 dark:text-frost-muted">
              {items.length} {items.length === 1 ? 'cliente cadastrado' : 'clientes cadastrados'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setMapOpen(true)}
            className="inline-flex items-center gap-2 rounded-2xl border border-ink/10 bg-white/90 px-4 py-2 text-sm font-medium hover:bg-ink/5 dark:border-white/10 dark:bg-white/10 dark:hover:bg-white/20"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0Z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            Localização
          </button>
        </div>
        <div className="mt-4 relative max-w-md">
          <svg
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink/40 dark:text-frost-muted"
            width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            placeholder="Buscar por nome, e-mail ou telefone..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full rounded-2xl border border-ink/10 bg-white/90 py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20 dark:border-white/10 dark:bg-white/10"
          />
        </div>
      </div>

      {mapOpen && <CustomersMapModal tenantId={tenantId} onClose={() => setMapOpen(false)} />}
      {editing && (
        <CustomerEditModal
          tenantId={tenantId}
          customer={editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {items.map((c) => {
          const hue = avatarHue(c.nome);
          return (
            <div
              key={c.id}
              className="group flex flex-col gap-3 rounded-3xl border border-ink/10 bg-white/80 p-5 transition hover:border-accent/30 hover:shadow-lg hover:shadow-accent/5 dark:border-white/10 dark:bg-white/5"
            >
              <div className="flex items-start gap-3">
                <div
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-sm font-bold text-white shadow-sm"
                  style={{ backgroundColor: `hsl(${hue} 65% 48%)` }}
                  aria-hidden="true"
                >
                  {initials(c.nome)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold">{c.nome}</div>
                  <div className="mt-0.5 flex flex-col gap-0.5 text-xs text-ink/55 dark:text-frost-muted">
                    {c.email && (
                      <span className="inline-flex items-center gap-1.5 truncate">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <rect x="2" y="4" width="20" height="16" rx="2" />
                          <path d="m22 7-10 5L2 7" />
                        </svg>
                        <span className="truncate">{c.email}</span>
                      </span>
                    )}
                    {c.telefone && (
                      <span className="inline-flex items-center gap-1.5">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.94.36 1.86.7 2.73a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.35-1.27a2 2 0 0 1 2.11-.45c.87.34 1.79.57 2.73.7A2 2 0 0 1 22 16.92Z" />
                        </svg>
                        {c.whatsapp_country_code ? `${c.whatsapp_country_code} ` : ''}
                        {c.telefone}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setEditing(c)}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-ink/10 px-3 py-1.5 text-xs font-medium transition hover:border-accent/40 hover:bg-accent/5 hover:text-accent dark:border-white/10 dark:hover:bg-white/10"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                  </svg>
                  Editar
                </button>
              </div>

              {c.endereco && (
                <div className="flex items-start gap-1.5 text-xs text-ink/55 dark:text-frost-muted">
                  <svg className="mt-0.5 shrink-0" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0Z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <span>{c.endereco}</span>
                </div>
              )}

              <label
                className="mt-auto flex cursor-pointer items-center gap-2 rounded-2xl border border-ink/10 bg-ink/[0.02] px-3 py-2 text-xs font-medium text-ink/70 transition hover:bg-ink/5 dark:border-white/10 dark:bg-white/[0.03] dark:text-frost-muted dark:hover:bg-white/10"
                title="Permite que este cliente baixe o catálogo em Excel (.xlsx) na loja"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-accent"
                  checked={Boolean(c.can_download_catalog)}
                  disabled={savingId === c.id}
                  onChange={(e) => toggleCatalogAccess(c, e.target.checked)}
                />
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
                  <path d="M14 3v5h5" />
                </svg>
                Pode baixar catálogo (Excel)
              </label>
            </div>
          );
        })}

        {!items.length && (
          <p className="col-span-full rounded-3xl border border-dashed border-ink/15 px-5 py-12 text-center text-sm text-ink/50 dark:border-white/10 dark:text-frost-muted">
            {loading ? 'Carregando…' : 'Nenhum cliente encontrado.'}
          </p>
        )}
      </div>
    </div>
  );
}
